import React, { useState, useEffect, useRef } from 'react';
import MobileLayout from '../../../components/layout/MobileLayout';
import { useToast } from '../../../components/ui/Toast';
import { useI18n } from '../../../i18n/I18n';
import { getAPIKeys, generateAPIKey, deleteAPIKey, getWebhookApiKey, getMerchantPaymentSettings, updateMerchantPaymentSettings, getAuthorizedAgents, revokeAuthorizedAgent } from '../../../services/useAPICaller';
import { AuthorizedAgent, PaymentSettings } from '../../../types/payment.types';
import AddEndpointModal from '../../../components/ui/AddEndpointModal';
import ConfirmModal from '../../../components/ui/ConfirmModal';

/** Scopes that let an agent change the account rather than only read it. The MCP server treats
 *  these as step-up (`keys:write` above all), and a merchant deciding whether to revoke needs the
 *  same distinction at a glance. */
const ELEVATED_SCOPE = /:(write|revoke)$/;

/**
 * The trust anchor of an agent row. `client_id` is a Client ID Metadata Document URL, so its host
 * is who actually published the client — the display name comes from that same document and proves
 * nothing. Shown whole rather than trimmed to a registrable domain: `agents.example.com` and
 * `example.com` are different publishers, and guessing eTLD+1 without the public suffix list gets
 * `example.co.uk` wrong.
 */
function clientDomain(clientId: string): string {
    try {
        return new URL(clientId).hostname;
    } catch {
        return clientId;
    }
}

/** Timestamps arrive as `Z`-suffixed ISO instants, so this renders in the merchant's own timezone. */
function formatStamp(iso?: string): string | null {
    return iso ? new Date(iso).toLocaleString() : null;
}

export default function DevelopersView() {
    const { t } = useI18n();
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [apiKeys, setApiKeys] = useState<string[]>([]);
    const [webhookKey, setWebhookKey] = useState<string>("");
    const [paymentSettings, setPaymentSettings] = useState<PaymentSettings | null>(null);
    const [isEndpointModalOpen, setIsEndpointModalOpen] = useState(false);
    const [agents, setAgents] = useState<AuthorizedAgent[]>([]);
    const [agentToRevoke, setAgentToRevoke] = useState<AuthorizedAgent | null>(null);

    const toast = useToast();

    // The mount fetch reports failures through the resolver, but must not re-run when the merchant
    // switches language: `t` is rebuilt on every language change, so depending on it would refetch
    // every credential and flash the loading state. Reading it through a ref keeps the effect's
    // dependency list honest without making the effect language-reactive.
    const tRef = useRef(t);
    useEffect(() => {
        tRef.current = t;
    }, [t]);

    // Fetch Data
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                const keysResponse = await getAPIKeys();
                if (keysResponse && keysResponse.data) {
                    let keysData: string[] = [];
                    if (Array.isArray(keysResponse.data)) {
                        keysData = keysResponse.data;
                    } else if (typeof keysResponse.data === 'string') {
                        keysData = [keysResponse.data];
                    }
                    setApiKeys(keysData);
                }

                await getMerchantPaymentSettings((data: PaymentSettings) => {
                    setPaymentSettings(data);
                });

                const webhookKeyData = await getWebhookApiKey();
                setWebhookKey(webhookKeyData);

                const agentsResponse = await getAuthorizedAgents();
                setAgents(agentsResponse.data || []);

            } catch (error) {
                console.error("Error fetching developer data:", error);
                toast({ title: tRef.current('developers.errorGeneratingKey'), status: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [toast]);

    const handleGenerateKey = async () => {
        setGenerating(true);
        try {
            await generateAPIKey(async (newData) => {
                // Let's refetch all to be safe and consistent
                const res = await getAPIKeys();
                if (res && res.data) {
                    const keysData = Array.isArray(res.data) ? res.data : [res.data];
                    setApiKeys(keysData);
                }
            });
            toast({ title: t('developers.apiKeyGenerated'), status: "success" });
        } catch (error) {
            console.error("Error generating key:", error);
            toast({ title: t('developers.errorGeneratingKey'), status: "error" });
        } finally {
            setGenerating(false);
        }
    };

    const handleDeleteKey = async (key: string) => {
        try {
            await deleteAPIKey(key);
            setApiKeys(prev => prev.filter(k => k !== key));
            toast({ title: t('developers.apiKeyDeleted'), status: "success" });
        } catch (error) {
            console.error("Error deleting key:", error);
            toast({ title: t('developers.errorDeletingApiKey'), status: "error" });
        }
    };

    const handleAddEndpoint = async (url: string) => {
        if (!paymentSettings) return;
        try {
            const updatedSettings = { ...paymentSettings };
            const endpoints = updatedSettings.webhookEndpoints || [];
            endpoints.push(url);
            updatedSettings.webhookEndpoints = endpoints;

            await updateMerchantPaymentSettings(updatedSettings);
            setPaymentSettings(updatedSettings);
            setIsEndpointModalOpen(false);
            toast({ title: t('developers.endpointAdded'), status: "success" });
        } catch (error) {
            console.error("Error adding endpoint:", error);
            toast({ title: t('developers.errorAddingEndpoint'), status: "error" });
        }
    };

    const handleDeleteEndpoint = async (index: number) => {
        if (!paymentSettings || !paymentSettings.webhookEndpoints) return;
        try {
            const updatedSettings = { ...paymentSettings };
            updatedSettings.webhookEndpoints.splice(index, 1);

            await updateMerchantPaymentSettings(updatedSettings);
            setPaymentSettings(updatedSettings);
            toast({ title: t('developers.endpointDeleted'), status: "success" });
        } catch (error) {
            console.error("Error deleting endpoint:", error);
            toast({ title: t('developers.errorDeletingEndpoint'), status: "error" });
        }
    };

    /**
     * Revoking answers with the refreshed list, so the response drives state directly — no refetch
     * and no optimistic filter that could disagree with the server.
     */
    const handleRevokeAgent = async () => {
        if (!agentToRevoke) return;
        const revoked = agentToRevoke;
        setAgentToRevoke(null);
        try {
            const response = await revokeAuthorizedAgent(revoked.id);
            setAgents(response.data || []);
            toast({ title: t('developers.agents.revoked'), status: "success" });
        } catch (error) {
            console.error("Error revoking agent:", error);
            toast({ title: t('developers.agents.errorRevoking'), status: "error" });
        }
    };

    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text);
        toast({ title: `${label} copied`, status: "success", duration: 2000 });
    };

    const maskKey = (key: string) => {
        if (!key || key.length < 8) return key;
        return `${key.substring(0, 8)}...${key.substring(key.length - 8)}`;
    };

    return (
        <MobileLayout>
            {/* Header */}
            <div className="px-6 lg:px-10 pt-14 lg:pt-10 pb-2 z-10 w-full max-w-md lg:max-w-5xl bg-transparent">
                <button
                    onClick={() => window.history.back()}
                    className="w-10 h-10 rounded-full flex items-center justify-center text-txt-heading mb-4 hover:bg-surface-hover transition-colors cursor-pointer border-none lg:hidden"
                    style={{ background: 'var(--color-surface)' }}
                >
                    <span className="material-symbols-outlined text-[20px]">arrow_back</span>
                </button>
                <h1 className="text-3xl font-bold text-txt-heading tracking-tight leading-tight">{t('developers.title')}</h1>
                <p className="text-txt-secondary text-[14px] font-medium mt-1">{t('developers.subtitle')}</p>
            </div>

            {/* Content */}
            <div className="flex-1 w-full max-w-md lg:max-w-5xl overflow-y-auto no-scrollbar px-6 lg:px-10 pb-32 lg:pb-10 z-10 space-y-8">

                {loading ? (
                    <div className="text-txt-heading text-center mt-10">{t('developers.loading')}</div>
                ) : (
                    <>
                        <div className="lg:grid lg:grid-cols-2 lg:gap-8 space-y-8 lg:space-y-0">
                            {/* API Keys Section */}
                            <div className="space-y-4">
                                <div className="flex justify-between items-end">
                                    <h2 className="text-xl font-bold text-txt-heading">{t('developers.apiKeys')}</h2>
                                    <button
                                        onClick={handleGenerateKey}
                                        disabled={generating}
                                        className="bg-electric-blue hover:bg-electric-blue/90 disabled:bg-electric-blue/50 text-white text-xs font-bold py-2 px-4 rounded-lg shadow-lg shadow-electric-blue/20 transition-all active:scale-95 flex items-center gap-1 border-none cursor-pointer"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">add</span>
                                        {t('developers.newApiKey')}
                                    </button>
                                </div>

                                <div className="space-y-3">
                                    {apiKeys.length === 0 ? (
                                        <div className="text-txt-muted text-sm text-center py-4 rounded-2xl" style={{ background: 'var(--color-surface)' }}>{t('developers.noApiKeysMessage')}</div>
                                    ) : (
                                        apiKeys.map((key, index) => (
                                            <div key={index} className="rounded-2xl p-4 relative overflow-hidden group" style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                                                {/* Active Status */}
                                                <div className="flex items-center gap-2 mb-3">
                                                    <div className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.5)]"></div>
                                                    <span className="text-txt-secondary text-xs font-bold uppercase tracking-wider">Active</span>
                                                </div>

                                                {/* Key Display */}
                                                <div className="rounded-xl p-3 flex items-center justify-between" style={{ background: 'var(--badge-bg)', border: '1px solid var(--divider)' }}>
                                                    <span className="font-mono text-txt-heading text-sm mr-2 break-all">
                                                        {maskKey(key)}
                                                    </span>
                                                    <div className="flex items-center gap-1">
                                                        <button
                                                            onClick={() => copyToClipboard(key, "API Key by Code")}
                                                            className="text-txt-muted hover:text-txt-heading transition-colors p-1.5 rounded-lg hover:bg-surface-hover border-none cursor-pointer"
                                                            title="Copy API Key"
                                                        >
                                                            <span className="material-symbols-outlined text-[18px]">content_copy</span>
                                                        </button>

                                                        <button
                                                            onClick={() => handleDeleteKey(key)}
                                                            className="text-txt-muted hover:text-red-400 transition-colors p-1.5 rounded-lg hover:bg-red-500/10 border-none cursor-pointer"
                                                            title="Delete"
                                                        >
                                                            <span className="material-symbols-outlined text-[18px]">delete</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            {/* Webhooks Section */}
                            <div className="space-y-4 mt-8 lg:mt-0">
                                <div className="flex justify-between items-end">
                                    <h2 className="text-xl font-bold text-txt-heading">{t('developers.webhooks')}</h2>
                                    <button
                                        onClick={() => setIsEndpointModalOpen(true)}
                                        className="bg-electric-blue hover:bg-electric-blue/90 disabled:bg-electric-blue/50 text-white text-xs font-bold py-2 px-4 rounded-lg shadow-lg shadow-electric-blue/20 transition-all active:scale-95 flex items-center gap-1 border-none cursor-pointer"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">link</span>
                                        {t('developers.addEndpoint')}
                                    </button>
                                </div>

                                {/* Webhooks API Key Card */}
                                <div className="rounded-2xl p-1 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border border-indigo-500/30">
                                    <div className="bg-deep-navy rounded-xl p-4 backdrop-blur-sm">
                                        <p className="text-txt-secondary text-xs mb-2">{t('developers.webhookApiKey')}</p>
                                        <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-3 flex items-center justify-between">
                                            <span className="font-mono text-indigo-300 text-sm font-bold truncate mr-2">
                                                {webhookKey ? maskKey(webhookKey) : t('developers.loading')}
                                            </span>
                                            <button
                                                onClick={() => webhookKey && copyToClipboard(webhookKey, "Secret Key")}
                                                className="text-indigo-400 hover:text-indigo-200 transition-colors p-1 rounded hover:bg-indigo-500/20 border-none cursor-pointer"
                                            >
                                                <span className="material-symbols-outlined text-[18px]">content_copy</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Endpoints List */}
                                {paymentSettings?.webhookEndpoints?.map((endpoint, index) => (
                                    <div key={index} className="rounded-2xl p-4 flex items-center justify-between group" style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                                        <div className="flex items-center gap-3 overflow-hidden">
                                            <span className="material-symbols-outlined text-txt-muted text-[20px]">webhook</span>
                                            <span className="text-txt-secondary text-sm truncate font-medium">
                                                {endpoint}
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => handleDeleteEndpoint(index)}
                                            className="text-txt-muted hover:text-red-400 transition-colors p-1.5 rounded-lg hover:bg-red-500/10 border-none cursor-pointer"
                                            title="Delete"
                                        >
                                            <span className="material-symbols-outlined text-[18px]">delete</span>
                                        </button>
                                    </div>
                                ))}
                                {(!paymentSettings?.webhookEndpoints || paymentSettings.webhookEndpoints.length === 0) && (
                                    <div className="text-txt-muted text-sm text-center py-4 rounded-2xl" style={{ background: 'var(--color-surface)' }}>{t('developers.noWebhooksMessage')}</div>
                                )}
                            </div>
                        </div>

                        {/* Authorized agents — full width below the two-column grid (R10) */}
                        <div className="space-y-4">
                            <div>
                                <h2 className="text-xl font-bold text-txt-heading">{t('developers.agents.title')}</h2>
                                <p className="text-txt-secondary text-[13px] font-medium mt-1">{t('developers.agents.subtitle')}</p>
                            </div>

                            {agents.length === 0 ? (
                                <div className="text-txt-muted text-sm text-center py-4 rounded-2xl" style={{ background: 'var(--color-surface)' }}>{t('developers.agents.noAgentsMessage')}</div>
                            ) : (
                                <div className="space-y-3">
                                    {agents.map((agent) => {
                                        const domain = clientDomain(agent.clientId);
                                        const created = formatStamp(agent.createdDate);
                                        const lastUsed = formatStamp(agent.lastUsedAt);
                                        return (
                                            <div key={agent.id} className="rounded-2xl p-4" style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="min-w-0 space-y-3">
                                                        {/* Who this is: the name the client published, then the domain that vouches for it */}
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.5)] shrink-0"></div>
                                                                <span className="text-txt-heading text-[15px] font-bold truncate">{agent.clientName || domain}</span>
                                                            </div>
                                                            <div className="mt-2 rounded-xl px-3 py-1.5 inline-flex items-center gap-2" style={{ background: 'var(--badge-bg)', border: '1px solid var(--divider)' }}>
                                                                <span className="material-symbols-outlined text-txt-muted text-[14px]">public</span>
                                                                <span className="font-mono text-txt-secondary text-xs break-all">{domain}</span>
                                                            </div>
                                                        </div>

                                                        {/* What it is allowed to do */}
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <span className="text-txt-muted text-xs">{t('developers.agents.scopes')}</span>
                                                            {agent.scopes.map((scope) => (
                                                                <span
                                                                    key={scope}
                                                                    className={ELEVATED_SCOPE.test(scope)
                                                                        ? "font-mono text-[11px] px-2 py-1 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300"
                                                                        : "font-mono text-[11px] px-2 py-1 rounded-lg text-txt-secondary"}
                                                                    style={ELEVATED_SCOPE.test(scope) ? undefined : { background: 'var(--badge-bg)', border: '1px solid var(--divider)' }}
                                                                >
                                                                    {scope}
                                                                </span>
                                                            ))}
                                                        </div>

                                                        {/* Which keys this agent minted — the only place that question is answered */}
                                                        {agent.apiKeyLabels && agent.apiKeyLabels.length > 0 && (
                                                            <div className="flex flex-wrap items-center gap-2">
                                                                <span className="text-txt-muted text-xs">{t('developers.agents.mintedKeys')}</span>
                                                                {agent.apiKeyLabels.map((label, index) => (
                                                                    <span key={index} className="text-[11px] px-2 py-1 rounded-lg text-txt-secondary" style={{ background: 'var(--badge-bg)', border: '1px solid var(--divider)' }}>
                                                                        {label}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}

                                                        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-txt-muted">
                                                            <span>{t('developers.agents.authorized')} {created ?? '—'}</span>
                                                            <span>{t('developers.agents.lastUsed')} {lastUsed ?? t('developers.agents.never')}</span>
                                                        </div>
                                                    </div>

                                                    <button
                                                        onClick={() => setAgentToRevoke(agent)}
                                                        className="shrink-0 flex items-center gap-1 text-xs font-bold text-txt-muted hover:text-red-400 bg-transparent transition-colors py-2 px-3 rounded-lg hover:bg-red-500/10 border-none cursor-pointer"
                                                    >
                                                        <span className="material-symbols-outlined text-[18px]">link_off</span>
                                                        {t('developers.agents.revoke')}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {agents.length > 0 && (
                                <p className="text-txt-muted text-xs">{t('developers.agents.revocationNote')}</p>
                            )}
                        </div>

                        {/* Documentation Link */}
                        <div className="pt-4 pb-8 flex justify-center">
                            <p className="text-sm text-gray-600">
                                {t('developers.learnWebhooks')}{' '}
                                <a 
                                    href="https://docs.sprintcheckout.com/overview" 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="text-electric-blue hover:text-electric-blue/80 transition-colors text-sm font-bold no-underline"
                                >
                                    documentation
                                </a>
                            </p>
                        </div>
                    </>
                )}
            </div>

            {/* Modals */}
            <AddEndpointModal
                isOpen={isEndpointModalOpen}
                onClose={() => setIsEndpointModalOpen(false)}
                onConfirm={handleAddEndpoint}
            />
            <ConfirmModal
                isOpen={agentToRevoke !== null}
                titleKey="developers.agents.revokeTitle"
                messageKey="developers.agents.revokeMessage"
                confirmKey="developers.agents.revokeConfirm"
                cancelKey="developers.agents.revokeCancel"
                detail={agentToRevoke ? (agentToRevoke.clientName || clientDomain(agentToRevoke.clientId)) : undefined}
                destructive
                onConfirm={handleRevokeAgent}
                onClose={() => setAgentToRevoke(null)}
            />
        </MobileLayout>
    );
}
